import React, { useState, useRef, useImperativeHandle, useMemo } from 'react';
import { connect } from 'react-redux';
import { Space, Button, Spin, Popconfirm, message, Tabs } from 'antd';
import intl from 'react-intl-universal';

import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';

import { format2FullTimeStr } from '@/utils/date';

import * as HandoverApi from '@/api/handover';
import {
  initHandoverInfo,
  resetHandoverInfo,
  initHandoverBaseBusiness,
  initHandoverMemberCard,
} from '@/redux/actions/handover';

import styles from './index.module.scss';

import Overview from './components/Overview';
import BillPortal from './components/BillPortal';
import { HandoverInfo, DrawerContext } from './helper';

const Handover = (props) => {
  const { myRef, userInfo } = props;

  const [submitLoading, setSubmitLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [handoverInfo, setHandoverInfo] = useState<HandoverInfo>({} as HandoverInfo);
  const [categoryTab, setCategoryTab] = useState(null);
  const shiftTextRef = useRef('');

  const drawerRef = useRef<IdrawerRef>();

  // 更新交班备注
  const updateShiftText = (text) => {
    shiftTextRef.current = text;
  };

  const { setLoadingStatus, getHandoverInfo } = useMemo(() => {
    return {
      setLoadingStatus: (status) => {
        setDrawerLoading(status);
      },
      getHandoverInfo: async () => {
        try {
          setDrawerLoading(true);
          const res = await HandoverApi.fetchHandoverInfo();
          setHandoverInfo(res.data);
          updateShiftText(res.data.shiftText);
          setDrawerLoading(false);
        } catch (e) {
          //
        }
      },
    };
  }, []);

  const handleOpenDrawer = () => {
    drawerRef.current?.openDrawer();
    // props.initHandoverInfo();
    // props.initHandoverBaseBusiness();
    // props.initHandoverMemberCard();
    setCategoryTab('overview');
  };

  const submit = async () => {
    try {
      setSubmitLoading(true);
      const res = await HandoverApi.submitHandover({
        id: handoverInfo?.id,
        handInPettyCash: handoverInfo?.handInPettyCash, // 上交现金
        nextPettyCash: handoverInfo?.nextPettyCash, // 备用金
        shiftText: shiftTextRef.current,
      });
      if (res && res.status) {
        message.success(intl.get('handover_message_handover_success'));
        drawerRef.current?.closeDrawer();
        // props.toggleHandoverModalVisible(false);
        props.handoverCallback();
        setCategoryTab(null);
      }
      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
    }
  };

  const closeModal = () => {
    drawerRef.current?.closeDrawer();
    // props.toggleHandoverModalVisible(false);
    // props.resetHandoverInfo();
    setCategoryTab(null);
  };

  useImperativeHandle(
    myRef,
    () => ({
      ...drawerRef.current,
      openDrawer: handleOpenDrawer,
    }),
    [],
  );

  const modalFooter = () => {
    return (
      <Space>
        <Popconfirm
          title={() => {
            return userInfo?.userId === handoverInfo.employeeNo
              ? intl.get('handover_drawer_handover_confirm_self')
              : intl.get('handover_drawer_handover_confirm_other', { name: userInfo?.name });
          }}
          placement="topRight"
          okButtonProps={{
            loading: submitLoading,
          }}
          onConfirm={() => {
            submit();
          }}
        >
          <Button type="primary" style={{ color: 'white' }} loading={submitLoading}>
            {intl.get('handover_drawer_handover_btn')}
          </Button>
        </Popconfirm>
        <Button onClick={() => closeModal()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
    // return null;
  };

  return (
    <CustomDrawer
      ref={drawerRef}
      title={intl.get('handover_drawer_title')}
      footer={modalFooter()}
      onClose={closeModal}
      forceRender={false}
    >
      <DrawerContext.Provider
        value={{ setLoadingStatus, getHandoverInfo, handoverId: handoverInfo.id }}
      >
        <Spin spinning={drawerLoading}>
          <div className={styles['block-item']}>
            <label>{intl.get('handover_drawer_classes_name')}</label>
            <span>{handoverInfo.employeeName || '-'}</span>
          </div>
          <div className={styles['block-item']}>
            <label>{intl.get('handover_drawer_classes_time')}</label>
            <span>{format2FullTimeStr(handoverInfo.startTime)}</span>
            <span style={{ margin: '0 5px' }}>~</span>
            <span>
              {handoverInfo.endTime
                ? format2FullTimeStr(handoverInfo.endTime)
                : intl.get('handover_drawer_classes_time_now')}
            </span>
          </div>
          <Tabs
            className={styles.categoryTopTab}
            destroyInactiveTabPane
            type="card"
            activeKey={categoryTab}
            onChange={setCategoryTab}
            items={[
              {
                key: 'overview',
                label: intl.get('handover_drawer_category_overview'),
                children: (
                  <Overview handoverInfo={handoverInfo} updateShiftText={updateShiftText} />
                ),
              },
              {
                key: 'bill',
                label: intl.get('handover_drawer_category_bill'),
                children: <BillPortal handoverInfo={handoverInfo} />,
              },
            ]}
          />
        </Spin>
      </DrawerContext.Provider>
    </CustomDrawer>
  );
};

const mapStateToProps = (state) => {
  const { userInfo } = state.user;
  return { ...state.handover, userInfo };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  // toggleHandoverModalVisible,
  initHandoverInfo,
  resetHandoverInfo,
  initHandoverBaseBusiness,
  initHandoverMemberCard,
})(React.memo(Handover));

export default React.forwardRef<IdrawerRef, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
